import { EllipsisMenuItem } from '@gainsystems/gains-ui-commons';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { GainsToastService } from 'app/shared/services/gains-toast-service/gains-toast.service';
import { ArtifactBulkDeleteResult, DeletedArtifactRef, DEOAgentArtifact, IResponse } from '../../models/chat.models';
import { AIAgentService } from '../../services/ai-agent.service';
import { DeoOwnershipService } from '../../services/deo-ownership.service';
import { getApiErrorMessage, getResponseErrorMessage } from '../../services/api-error.helper';
import { DEOAiAssistantService } from '../../services/deo-ai-assistant.service';
import { GainsApplyCancelService } from 'app/shared/services/gains-apply-delete-service/gains-apply-delete.service';

const LOAD_FAILED_MESSAGE = 'Failed to load artifacts. Please try again.';

interface ArtifactCard {
  id: string;
  sessionId: string;
  name: string;
  type: string;
  createdAt: string;
  htmlContent?: string;
  /** False for another user's artifact in a project shared with "Everyone": it can be viewed and downloaded only. */
  isMine: boolean;
}

@Component({
  standalone: false,
  selector: 'deo-artifacts',
  templateUrl: './deo-artifacts.component.html',
  styleUrls: ['./deo-artifacts.component.scss']
})
export class DeoArtifactsComponent implements OnInit, OnChanges {
  @Input() projectId: string | null = null;
  @Input() isActive = true;
  /** Changed by the host when the artifact list moved on; this panel reloads when next shown, never while hidden. */
  @Input() refreshToken = 0;
  @Output() loadingChange = new EventEmitter<boolean>();
  /**
   * Artifacts that are no longer on the backend after a delete, each paired with its owning chat. This
   * screen owns only its own list, while the host owns the artifact totals shown against each chat, so
   * without this the sidebar count keeps the pre-delete number until the whole screen is reloaded.
   */
  @Output() artifactsDeleted$ = new EventEmitter<DeletedArtifactRef[]>();

  searchTerm = '';
  isLoading = false;

  /** Backend failure message for the last load, or null when it succeeded. Drives the inline error state. */
  loadError: string | null = null;

  artifacts: ArtifactCard[] = [];
  /** The only record of what is selected — the cards themselves carry no selected flag. */
  selectedArtifactIds: string[] = [];

  artifactsMenuItems: EllipsisMenuItem[] = [
    { title: 'Download', icon: 'download.svg' },
    { title: 'Delete', warning: true, icon: 'trash-2.svg' }
  ];

  readonly sharedArtifactMenuItems: EllipsisMenuItem[] = this.artifactsMenuItems.filter(item => item.title !== 'Delete');

  /** refreshToken the list currently held was loaded for; null until the first load. */
  private _loadedRefreshToken: number | null = null;
  private _hasInitialized = false;

  constructor(
    private readonly _toastService: GainsToastService,
    private readonly _aiAgentService: AIAgentService,
    private readonly _deoAiAssistantService: DEOAiAssistantService,
    private readonly _gainsApplyCancelService: GainsApplyCancelService,
    private readonly _ownershipService: DeoOwnershipService
  ) {}

  ngOnInit(): void {
    if (this.isActive) {
      this.loadArtifacts();
    }
    this._hasInitialized = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this._hasInitialized || !this.isActive) {
      return;
    }

    if (changes['isActive'] || changes['refreshToken']) {
      this.onPanelShown();
    }
  }

  /** The host raises its spinner on every tab switch, so a visit with nothing to reload still has to lower it. */
  private onPanelShown(): void {
    if (this._loadedRefreshToken === this.refreshToken) {
      this.loadingChange.emit(this.isLoading);
      return;
    }

    this.loadArtifacts();
  }

  get filteredArtifacts(): ArtifactCard[] {
    const query = this.searchTerm.trim().toLowerCase();

    if (!query) {
      return this.artifacts;
    }

    return this.artifacts.filter(artifact => artifact.name.toLowerCase().includes(query));
  }

  trackByArtifactId(_: number, artifact: ArtifactCard): string {
    return artifact.id;
  }

  /** Re-runs the load after a failure, from the inline error state's Try again button. */
  retryLoad(): void {
    this.loadArtifacts();
  }

  private loadArtifacts(): void {
    this._loadedRefreshToken = this.refreshToken;
    this.isLoading = true;
    this.loadError = null;
    this.loadingChange.emit(true);

    this.getArtifactsRequest().subscribe({
      next: response => {
        if (!response.succeeded) {
          this.failLoading(getResponseErrorMessage(response, LOAD_FAILED_MESSAGE));
          return;
        }

        this.artifacts = (response.data ?? []).map(artifact => this.mapArtifactToCard(artifact));
        this.finishLoading();
      },
      error: error => {
        this.failLoading(getApiErrorMessage(error, LOAD_FAILED_MESSAGE));
      }
    });
  }

  /**
   * Surfaces a failed load in the panel itself rather than as a toast: a toast auto-dismisses and leaves
   * the empty state claiming the user owns no artifacts, with nothing left to retry.
   */
  private failLoading(message: string): void {
    this.artifacts = [];
    this.loadError = message;
    this.finishLoading();
  }

  /** Clears the in-flight flag and notifies the parent so the right-panel spinner hides. */
  private finishLoading(): void {
    this.isLoading = false;
    this.loadingChange.emit(false);
  }

  /** Loads artifacts for a single project when scoped to one, otherwise every artifact across all sessions. */
  private getArtifactsRequest(): Observable<IResponse<DEOAgentArtifact[]>> {
    if (!this.projectId) {
      return this._aiAgentService.getAllArtifacts();
    }

    return forkJoin({
      response: this._aiAgentService.getProjectArtifacts(this.projectId),
      projectSessionIds: this.getProjectSessionIds(this.projectId)
    }).pipe(map(({ response, projectSessionIds }) => this.scopeArtifactsToProjectSessions(response, projectSessionIds)));
  }

  /**
   * Session ids owned by the project, or null when they cannot be determined.
   *
   * The backend artifacts-by-project endpoint currently answers with every artifact of the user regardless
   * of the project, so the list is re-scoped client-side. Returning null (failed or unsuccessful sessions
   * call) means "cannot scope safely" and leaves the backend response untouched, so a hiccup on this call
   * never hides real artifacts.
   */
  private getProjectSessionIds(projectId: string): Observable<Set<string> | null> {
    return this._aiAgentService.getProjectSessions(projectId).pipe(
      map(response => (response?.succeeded ? new Set((response.data ?? []).map(session => session.id)) : null)),
      catchError(() => of(null))
    );
  }

  /** Drops artifacts whose owning chat does not belong to the project; a no-op once the backend scopes them itself. */
  private scopeArtifactsToProjectSessions(response: IResponse<DEOAgentArtifact[]>, projectSessionIds: Set<string> | null): IResponse<DEOAgentArtifact[]> {
    if (!response?.succeeded || !projectSessionIds) {
      return response;
    }

    return { ...response, data: (response.data ?? []).filter(artifact => projectSessionIds.has(artifact.sessionId)) };
  }

  private mapArtifactToCard(artifact: DEOAgentArtifact): ArtifactCard {
    return {
      id: artifact.id,
      sessionId: artifact.sessionId,
      name: artifact.title,
      type: artifact.type,
      createdAt: this.formatArtifactDate(artifact.createdAt),
      htmlContent: artifact.htmlContent,
      isMine: this._ownershipService.isMine(artifact.userId)
    };
  }

  private formatArtifactDate(createdAt: Date): string {
    const artifactDate = new Date(createdAt);
    if (Number.isNaN(artifactDate.getTime())) {
      return '';
    }

    const month = `${artifactDate.getMonth() + 1}`.padStart(2, '0');
    const day = `${artifactDate.getDate()}`.padStart(2, '0');
    const year = artifactDate.getFullYear();
    return `${month}/${day}/${year}`;
  }

  isArtifactSelected(artifactId: string): boolean {
    return this.selectedArtifactIds.includes(artifactId);
  }

  /** Space would otherwise toggle the input natively, which fights the [checked] binding. */
  onCheckboxKey(event: Event, artifactId: string): void {
    event.preventDefault();
    this.toggleArtifactSelection(artifactId, event);
  }

  toggleArtifactSelection(artifactId: string, event: Event): void {
    event?.stopPropagation();
    const index = this.selectedArtifactIds.indexOf(artifactId);
    if (index > -1) {
      this.selectedArtifactIds.splice(index, 1);
    } else {
      this.selectedArtifactIds.push(artifactId);
    }
  }

  onArtifactMenuAction(event: Event | string, artifact: ArtifactCard): void {
    if (event === 'Download') {
      this.downloadArtifact(artifact);
    }

    if (event === 'Delete' && artifact.isMine) {
      this.deleteSingleArtifactConfirmation(artifact);
    }
  }

  viewArtifact(artifact: ArtifactCard): void {
    this._deoAiAssistantService.openArtifactPreview({
      id: artifact.id,
      title: artifact.name,
      type: artifact.type,
      htmlContent: artifact.htmlContent || '',
      createdAt: artifact.createdAt
    });
  }

  /**
   * A successful response means the artifact is gone even when `data` is false — false only says nothing
   * matched the id, so it was already deleted elsewhere. Only a non-2xx (the error callback) means it is
   * still there, which is why nothing here branches on `response.data`.
   */
  deleteArtifact(artifact: ArtifactCard): void {
    this._aiAgentService.deleteArtifact(artifact.id).subscribe({
      next: () => {
        this._toastService.success('Artifact deleted', `"${artifact.name}" was deleted.`);
        this.artifactsDeleted$.emit([{ id: artifact.id, sessionId: artifact.sessionId }]);
        this.deselectArtifacts([artifact.id]);
        this.closeArtifactPreviewIfDeleted([artifact.id]);
        this.loadArtifacts();
      },
      error: error => {
        this._toastService.error('Error', getApiErrorMessage(error, 'Failed to delete artifact'));
      }
    });
  }

  downloadArtifact(artifact?: ArtifactCard): void {
    if (!artifact?.htmlContent) {
      return;
    }

    this._deoAiAssistantService.downloadArtifactContent(artifact.name, artifact.htmlContent, artifact.type);
  }

  deleteSelectedArtifacts(event: Event): void {
    const selectedArtifacts = this.getSelectedArtifacts();
    if (selectedArtifacts.length === 0) {
      return;
    }
    if (selectedArtifacts.length === 1) {
      this.deleteSingleArtifactConfirmation(selectedArtifacts[0]);
    } else {
      this.deleteMultiArtifactsConfirmation(event, selectedArtifacts);
    }
  }

  /**
   * Resolves the selected ids against the current list. Ids with no matching card are dropped rather than
   * carried as holes, so a selection made before a reload that removed an artifact cannot delete by id alone.
   */
  private getSelectedArtifacts(): ArtifactCard[] {
    return this.selectedArtifactIds.map(id => this.artifacts.find(artifact => artifact.id === id)).filter((artifact): artifact is ArtifactCard => !!artifact);
  }

  /**
   * Drops deleted ids from the selection. The reload that follows a delete rebuilds the cards, but the
   * selection is held separately and would otherwise keep the deleted ids — leaving the bulk-actions drawer
   * open, counting artifacts that no longer exist, with a Delete button that resolves to nothing.
   */
  private deselectArtifacts(artifactIds: string[]): void {
    this.selectedArtifactIds = this.selectedArtifactIds.filter(id => !artifactIds.includes(id));
  }

  private deleteSingleArtifactConfirmation(artifact: ArtifactCard): void {
    this._gainsApplyCancelService.delete(
      'Delete Artifact',
      `<div><div>Are you sure you want to delete artifact "${artifact.name}"? 
      </div><div class="artifact-delete-warning-text">You cannot undo this action.</div></div>`,
      () => {
        this.deleteArtifact(artifact);
      },
      () => {
        // Cancel action
      }
    );
  }

  private deleteMultiArtifactsConfirmation(event: Event, selectedArtifacts: ArtifactCard[]): void {
    event.stopPropagation();

    this._gainsApplyCancelService.delete(
      'Delete Artifacts',
      `<div><div>Are you sure you want to delete the selected artifacts? 
      </div><div class="artifact-delete-warning-text">You cannot undo this action.</div></div>`,
      () => {
        this.onHandleDeletedMultiArtifacts(selectedArtifacts);
      },
      () => {
        // Cancel action
      }
    );
  }

  private onHandleDeletedMultiArtifacts(selectedArtifacts: ArtifactCard[]): void {
    const requestedIds = selectedArtifacts.map(artifact => artifact.id);
    const deletedRefs = selectedArtifacts.map(artifact => ({ id: artifact.id, sessionId: artifact.sessionId }));

    this._aiAgentService.bulkDeleteArtifact(requestedIds).subscribe({
      next: response => {
        this.reportBulkDeleteOutcome(response.data);
        // Every requested id is gone on a successful response — the ones in not_found_ids were simply
        // already deleted — so the host can drop them all from its counts.
        this.artifactsDeleted$.emit(deletedRefs);
        this.deselectArtifacts(requestedIds);
        this.closeArtifactPreviewIfDeleted(requestedIds);
        this.loadArtifacts();
      },
      error: error => {
        this._toastService.error('Error', getApiErrorMessage(error, 'Failed to delete selected artifacts'));
      }
    });
  }

  /**
   * Partial success is the backend's normal mode, so a 200 is reported by comparing deleted against
   * requested rather than assumed to have removed everything. Ids that matched nothing were already
   * deleted in another tab or session, and the reload that follows brings the list back in step.
   */
  private reportBulkDeleteOutcome(result: ArtifactBulkDeleteResult | null): void {
    if (!result || result.requested === 0) {
      return;
    }

    if (result.deleted === result.requested) {
      this._toastService.success('Artifacts deleted', `${result.deleted} ${this.artifactWord(result.deleted)} deleted.`);
      return;
    }

    if (result.deleted === 0) {
      this._toastService.success('Artifacts already removed', 'Those artifacts had already been deleted. The list has been refreshed.');
      return;
    }

    this._toastService.success(
      'Some artifacts deleted',
      `${result.deleted} of ${result.requested} artifacts deleted. The rest had already been removed, so the list has been refreshed.`
    );
  }

  private artifactWord(count: number): string {
    return count === 1 ? 'artifact was' : 'artifacts were';
  }

  /** This screen opens the preview, so it also closes it when the artifact behind it is deleted. */
  private closeArtifactPreviewIfDeleted(deletedArtifactIds: string[]): void {
    const openArtifactId = this._deoAiAssistantService.selectedArtifact?.id;

    if (openArtifactId && deletedArtifactIds.includes(openArtifactId)) {
      this._deoAiAssistantService.closeArtifactPreview();
    }
  }
}
